import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Camera, Package, FilePlus2, BookMarked, Image as ImageIcon, Settings, Loader2, Tag, CheckCircle2, FileEdit, ArrowRight } from "lucide-react";
import { fmtMoney, fileSrc, STATUS_META, fmtDate } from "./studioApi";

function Stat({ icon: Icon, label, value, to, testid, tone }) {
  return (
    <Link to={to} data-testid={testid} className="block">
      <Card className="p-5 h-full border-[#AD8642]/30 hover:shadow-md transition-shadow bg-white">
        <div className={`grid h-10 w-10 place-items-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></div>
        <div className="mt-4 text-4xl font-semibold tabular-nums text-[#153E32] leading-none" data-testid={`${testid}-value`}>{value}</div>
        <div className="mt-1 text-sm text-[#6A776F]">{label}</div>
      </Card>
    </Link>
  );
}

const ACTIONS = [
  { to: "/studio/products/new", label: "Add Tea Product", desc: "Upload the original tasting photograph, let AI read the label, review and save.", icon: Camera, testid: "sdash-add-product" },
  { to: "/studio/library", label: "Product Library", desc: "Browse by grade and price band, search lots, mark sold, select for a catalogue.", icon: Package, testid: "sdash-library" },
  { to: "/studio/catalogues/new", label: "Create Catalogue", desc: "Pick products, arrange, add a cover, preview and export landscape or portrait.", icon: FilePlus2, testid: "sdash-create" },
  { to: "/studio/catalogues", label: "Saved Catalogues", desc: "Reopen, duplicate, refresh from the library, download or share.", icon: BookMarked, testid: "sdash-catalogues" },
  { to: "/studio/covers", label: "Cover Page Library", desc: "Upload externally designed JPG, PNG or PDF covers.", icon: ImageIcon, testid: "sdash-covers" },
  { to: "/studio/settings", label: "Settings", desc: "Company details, GST rate, rating policy bands and attribution strip.", icon: Settings, testid: "sdash-settings" },
];

export default function StudioDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/studio/stats").then((r) => setStats(r.data)).catch(() => setStats({})); }, []);
  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#153E32]" /></div>;

  return (
    <div className="pgt-fade-up space-y-8" data-testid="studio-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#AD8642] font-semibold">AI Tea Catalogue Studio · {user?.name}</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#153E32] mt-1">Customer Product Profiles</h1>
          <p className="text-sm text-[#6A776F] mt-2 max-w-2xl">A second catalogue method: original photograph → AI-assisted reading → your review → reference-faithful landscape or portrait PDFs.</p>
        </div>
        <Button onClick={() => navigate("/studio/products/new")} data-testid="sdash-cta-add" className="bg-[#153E32] hover:bg-[#2A5144] gap-2"><Camera className="h-4 w-4" /> Add Tea Product</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Package} label="Available products" value={stats.available ?? 0} to="/studio/library?status=available" testid="sstat-available" tone="bg-emerald-100 text-emerald-800" />
        <Stat icon={Tag} label="Sold products" value={stats.sold ?? 0} to="/studio/library?status=sold" testid="sstat-sold" tone="bg-red-100 text-red-800" />
        <Stat icon={FileEdit} label="Draft catalogues" value={stats.drafts ?? 0} to="/studio/catalogues?status=draft" testid="sstat-drafts" tone="bg-amber-100 text-amber-800" />
        <Stat icon={CheckCircle2} label="Saved catalogues" value={stats.saved ?? 0} to="/studio/catalogues?status=saved" testid="sstat-saved" tone="bg-[#F1E5C9] text-[#153E32]" />
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {ACTIONS.map((a) => (
          <Link key={a.to} to={a.to} data-testid={a.testid} className="block group">
            <Card className="p-5 h-full border-[#AD8642]/30 bg-white hover:border-[#AD8642] transition-colors">
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#153E32] text-[#D6BC85] shrink-0"><a.icon className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-[#153E32] flex items-center gap-1">{a.label} <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                  <div className="text-xs text-[#6A776F] mt-1">{a.desc}</div>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 border-[#AD8642]/30 bg-white">
          <div className="flex items-center justify-between mb-3"><h2 className="font-display text-2xl text-[#153E32]">Recent products</h2><Link to="/studio/library" className="text-xs text-[#AD8642] font-semibold">View library</Link></div>
          <div className="space-y-2" data-testid="sdash-recent-products">
            {(stats.recent_products || []).length === 0 && <div className="text-sm text-[#6A776F]">No products yet — add your first tea.</div>}
            {(stats.recent_products || []).map((p) => (
              <button key={p.id} onClick={() => navigate(`/studio/products/${p.id}`)} className="w-full flex items-center gap-3 rounded-lg border border-[#AD8642]/20 px-3 py-2 text-left hover:bg-[#F7F7F3]" data-testid={`sdash-product-${p.id}`}>
                <div className="h-10 w-10 rounded bg-[#F1E5C9] overflow-hidden shrink-0">{p.photo?.url && <img src={fileSrc(p.photo.render_url || p.photo.url)} alt="" className="h-full w-full object-contain" />}</div>
                <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#153E32] truncate">{p.name}</div><div className="text-xs text-[#6A776F]">{p.grade || "—"} · {fmtMoney(p.price_per_kg)}/kg</div></div>
                <Badge variant="outline" className={STATUS_META[p.status]?.cls}>{STATUS_META[p.status]?.label || p.status}</Badge>
              </button>
            ))}
          </div>
        </Card>
        <Card className="p-5 border-[#AD8642]/30 bg-white">
          <div className="flex items-center justify-between mb-3"><h2 className="font-display text-2xl text-[#153E32]">Recent catalogues</h2><Link to="/studio/catalogues" className="text-xs text-[#AD8642] font-semibold">View all</Link></div>
          <div className="space-y-2" data-testid="sdash-recent-catalogues">
            {(stats.recent_catalogues || []).length === 0 && <div className="text-sm text-[#6A776F]">No catalogues yet.</div>}
            {(stats.recent_catalogues || []).map((c) => (
              <button key={c.id} onClick={() => navigate(`/studio/catalogues/${c.id}`)} className="w-full flex items-center gap-3 rounded-lg border border-[#AD8642]/20 px-3 py-2 text-left hover:bg-[#F7F7F3]" data-testid={`sdash-catalogue-${c.id}`}>
                <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#153E32] truncate">{c.title}</div><div className="text-xs text-[#6A776F] font-mono-id">{c.catalogue_number} · {c.product_count ?? (c.items || []).length} products · {fmtDate(c.updated_at)}</div></div>
                <Badge variant="outline" className={c.status === "saved" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}>{c.status}</Badge>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
